// Admin API client — /admin/audit-spend/* (mirrors the API's audit-spend aggregate).

import type {
  ApiEnvelope as AuditSpendEnvelope,
  BySourceData as AuditSpendBySourceData,
  CacheDeltaData as AuditSpendCacheDeltaData,
  CostBreachesData as AuditSpendCostBreachesData,
  DailyTrendData as AuditSpendDailyTrendData,
  HeadlineData as AuditSpendHeadlineData,
  HotCollectionsData as AuditSpendHotCollectionsData,
  HotEgressData as AuditSpendHotEgressData,
  OutlierDetailData as AuditSpendOutlierDetailData,
  OutliersData as AuditSpendOutliersData,
  ReconcileData as AuditSpendReconcileData,
} from '@/pages/audit-spend/types';
import { client, auditSpendUrl } from './transport';

export const auditSpendApi = {
  // Audit Spend Dashboard (A7Y-1494)
  //
  // Every method takes an optional `force`, which becomes `?force=1` and makes
  // the API bypass its 24h cache (source: a7y-api/src/routes/admin/audit-spend/index.ts). Only the dashboard's Refresh button sets it —
  // an ordinary page load should be answered from cache, that being the whole
  // point of the cache.
  getAuditSpendHeadline: (params?: { force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendHeadlineData>>(
      auditSpendUrl('/audit-spend/headline', params)
    ),
  getAuditSpendCacheDelta: (params?: { force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendCacheDeltaData>>(
      auditSpendUrl('/audit-spend/cache-delta', params)
    ),
  getAuditSpendDailyTrend: (params?: { days?: number; force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendDailyTrendData>>(
      auditSpendUrl('/audit-spend/daily-trend', params)
    ),
  getAuditSpendCostBreaches: (params?: { days?: number; limit?: number; force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendCostBreachesData>>(
      auditSpendUrl('/audit-spend/cost-breaches', params)
    ),
  getAuditSpendBySource: (params?: { force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendBySourceData>>(
      auditSpendUrl('/audit-spend/by-source', params)
    ),
  getAuditSpendOutliers: (params?: { threshold?: number; hours?: number; force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendOutliersData>>(
      auditSpendUrl('/audit-spend/outliers', params)
    ),
  getAuditSpendOutlierDetail: (auditId: string) =>
    client.get<AuditSpendEnvelope<AuditSpendOutlierDetailData>>(
      `/audit-spend/outlier-detail/${encodeURIComponent(auditId)}`
    ),
  getAuditSpendHotCollections: (params?: { hours?: number; force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendHotCollectionsData>>(
      auditSpendUrl('/audit-spend/hot-collections', params)
    ),
  getAuditSpendHotEgress: (params?: { hours?: number; force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendHotEgressData>>(
      auditSpendUrl('/audit-spend/hot-egress', params)
    ),
  getAuditSpendReconcile: (params?: { hours?: number; force?: boolean }) =>
    client.get<AuditSpendEnvelope<AuditSpendReconcileData>>(
      auditSpendUrl('/audit-spend/reconcile', params)
    ),
};
